import {
  DateFormatHelper,
  DateOptionsBuilder,
  DayFormats,
  WeekdayFormats,
  MonthFormats,
} from '@/ui-kit/helpers/DateFormatHelper';
import { DEFAULT_TIME_FORMAT } from '@/ui-kit/components/form/controls/components/time-picker/constants';
import ListingsApiService from '@/services/api/listings';
import { createDate } from '@/utils/date';
import { AVAILABLE_MINUTES_VALUES, DEFAULT_VIEWING_DURATION_IN_MINUTES } from '../constants';

const MIN_HOURS_FOR_ROAD = 1;
const DAYS_TO_FETCH = 7;
const BOOK_VIEWING_FORMAT = new DateOptionsBuilder()
  .addDayFormat(DayFormats.TwoDigit)
  .addMonthFormat(MonthFormats.Long)
  .addWeekdayFormat(WeekdayFormats.Long)
  .build();

export default class BookViewingAvailabilitiesHelper {
  static async fetchListingAvailability(listingId, from = this.#getInitialFromDate()) {
    const to = this.#getToDate(from);
    const intervals = await ListingsApiService.getListingAvailability(listingId, from, to);

    return {
      from,
      to,
      days: this.#mapIntervalsToDaysAvailability(intervals),
    };
  }

  static mergeAvailabilities(...availabilitiesItems) {
    return availabilitiesItems.reduce((accum, availabilities, index) => ({
      ...accum,
      from: index === 0 ? availabilities.from : accum.from,
      to: index === availabilitiesItems.length - 1 ? availabilities.to : accum.to,
      days: accum.days
        ? this.#mergeAvailabilitiesDays(accum.days, availabilities.days)
        : availabilities.days,
    }), {});
  }

  static #mergeAvailabilitiesDays(days1, days2) {
    days1.forEach((valueDays1, key) => {
      if (days2.has(key)) {
        const valueDays2 = days2.get(key);

        const newValue = {
          ...valueDays1,
          value: valueDays2.value,
          timeSlots: this.#mergeTimeSlots(valueDays1.timeSlots, valueDays2.timeSlots),
        };

        days1.set(key, newValue);
        days2.delete(key);
      }
    });

    return new Map([...days1.entries(), ...days2.entries()]);
  }

  static #mergeTimeSlots(timeSlots1, timeSlots2) {
    const timeSlots = [...timeSlots1];
    const lastTimeSlot = timeSlots.at(-1);

    timeSlots2.forEach((timeSlot) => {
      if (
        Number(timeSlot.hour) > Number(lastTimeSlot.hour)
        || (Number(timeSlot.hour) === Number(lastTimeSlot.hour)
        && Number(timeSlot.minutes) > Number(lastTimeSlot.minutes))
      ) {
        timeSlots.push(timeSlot);
      }
    });

    return timeSlots;
  }

  static #getInitialFromDate() {
    const date = createDate();
    const minute = date.minute();
    const isFirstHourPart = minute > AVAILABLE_MINUTES_VALUES.ZERO
      && minute <= AVAILABLE_MINUTES_VALUES.THIRTY;

    return date
      .minute(isFirstHourPart ? AVAILABLE_MINUTES_VALUES.THIRTY : AVAILABLE_MINUTES_VALUES.ZERO)
      .add(isFirstHourPart ? MIN_HOURS_FOR_ROAD : MIN_HOURS_FOR_ROAD + 1, 'hour');
  }

  static #getToDate(from) {
    return from.add(DAYS_TO_FETCH, 'day');
  }

  static #mapIntervalsToDaysAvailability(intervals) {
    return intervals.reduce((accum, interval) => {
      this.#cleanUpInterval(interval).forEach(([start, end]) => {
        const dayDescription = this.#getDayDescription(start);
        const availability = accum.get(dayDescription);
        const timeSlots = this.#getTimeSlots(start, end);

        if (timeSlots.length) {
          accum.set(dayDescription, {
            description: dayDescription,
            value: start,
            timeSlots: availability ? [...availability.timeSlots, ...timeSlots] : timeSlots,
          });
        }
      });

      return accum;
    }, new Map());
  }

  static #cleanUpInterval([start, end]) {
    return this.#hasEqualDay(start, end)
      ? [[start, end]]
      : [
        [start, this.#getEndOfDay(start)],
        ...this.#cleanUpInterval([this.#getStartOfNextDay(start), end]),
      ];
  }

  static #getTimeSlots(startDate, endDate) {
    let from = createDate({ datetime: startDate });
    const to = createDate({ datetime: endDate });
    const timeSlots = [];

    do {
      timeSlots.push(this.#getTimeSlot(from));
      from = from.add(DEFAULT_VIEWING_DURATION_IN_MINUTES, 'minute');
    } while (from <= to);

    return timeSlots;
  }

  static #getTimeSlot(date) {
    return {
      hour: date.format('HH'),
      minutes: date.format('mm'),
      description: date.format(DEFAULT_TIME_FORMAT),
    };
  }

  static #getDayDescription(date) {
    return DateFormatHelper.format(date, BOOK_VIEWING_FORMAT);
  }

  static #getStartOfNextDay(date) {
    const nextDay = createDate({ datetime: date }).add(1, 'day');

    return this.#getStartOfDay(nextDay);
  }

  static #getEndOfDay(date) {
    return createDate({ datetime: date })
      .endOf('day')
      .toISOString();
  }

  static #getStartOfDay(date) {
    return createDate({ datetime: date })
      .startOf('day')
      .toISOString();
  }

  static #hasEqualDay(left, right) {
    return new Date(left).getDay() === new Date(right).getDay();
  }
}
